import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  Button,
  MenuItem,
  Menu,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  MenuList,
  useMediaQuery,
  SwipeableDrawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  useScrollTrigger,
  Box,
  Collapse,
  Snackbar,
  Alert,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import logo from "../../assets/logo.jpg";
import AuthModal from "./AuthModal.jsx";
import ChangePasswordDialog from "./ChangePasswordDialog";
import {
  clearAuth,
  getAuthUser,
  isLoggedIn,
} from "../../services/tokenService";
import {
  isChatbotMaintenanceMode,
  MAINTENANCE_PAGE_PATH,
} from "../../config/features";

const ToolbarMargin = styled("div")(({ theme }) => ({
  ...theme.mixins.toolbar,
}));

const Logo = styled("img")(() => ({
  height: "3.5em",
  width: "auto",
}));

const TabStyled = styled(Tab)(() => ({
  minWidth: 10,
  textTransform: "none",
  fontSize: "0.7rem",
  fontWeight: 800,
  color: "#000",
  padding: "0.5rem 1rem",
  "&:hover": {
    color: "#000080",
  },
  "&.Mui-selected": {
    color: "#1976d2",
    fontWeight: 600,
  },
}));

const blink = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.2; }
  100% { opacity: 1; }
`;

const EstimateButton = styled(Button)(() => ({
  borderRadius: "10px",
  padding: "0.4em 1em",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.9rem",
  backgroundColor: "#1976d2",
  color: "#fff",
  "&:hover": {
    backgroundColor: "#115293",
  },
}));

const AppBarStyled = styled(AppBar)(() => ({
  zIndex: 1301,
  backgroundColor: "#fff",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  padding: "0 16px",
}));

const ElevationScroll = ({ children }) => {
  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 0 });
  return React.cloneElement(children, { elevation: trigger ? 4 : 0 });
};

const LabelWithArrow = ({ label, isOpen }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
    {label}
    <ExpandMoreIcon
      sx={{
        fontSize: "1rem",
        transition: "transform 0.2s ease",
        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
      }}
    />
  </Box>
);

// ✅ Case study sub-items (id-based dropdown)
const CASE_STUDIES = [
  { label: "Star Oil CRM", slug: "star-oil-crm" },
  { label: "GCS Enterprise Platform", slug: "gcs-enterprise-platform" },
  { label: "AI Tekkbot", slug: "ai-tekkbot" },
  // { label: "PITB Modernization", slug: "pitb-modernization" },
];

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const iOS =
    typeof navigator !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent);
  const location = useLocation();
  const navigate = useNavigate();

  const [value, setValue] = useState(false);
  const [homeAnchor, setHomeAnchor] = useState(null);
  const [openHomeMenu, setOpenHomeMenu] = useState(false);
  const [servicesAnchor, setServicesAnchor] = useState(null);
  const [openServicesMenu, setOpenServicesMenu] = useState(false);
  const [caseStudiesAnchor, setCaseStudiesAnchor] = useState(null);
  const [openCaseStudiesMenu, setOpenCaseStudiesMenu] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [authMenuAnchor, setAuthMenuAnchor] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [authDefaultView, setAuthDefaultView] = useState("signIn");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [caseStudiesDrawerOpen, setCaseStudiesDrawerOpen] = useState(false);
  const [authSuccessMessage, setAuthSuccessMessage] = useState("");

  const handleHomeHover = useCallback((e) => {
    setHomeAnchor(e.currentTarget);
    setOpenHomeMenu(true);
  }, []);

  const handleMenuClose = () => {
    setOpenHomeMenu(false);
  };

  const handleAuthIconClick = (e) => {
    setAuthMenuAnchor(e.currentTarget);
  };

  const handleAuthMenuClose = () => {
    setAuthMenuAnchor(null);
  };

  const openAuthWith = (view) => {
    handleAuthMenuClose();
    if (isChatbotMaintenanceMode) {
      if (location.pathname !== MAINTENANCE_PAGE_PATH) {
        navigate(MAINTENANCE_PAGE_PATH);
      }
      return;
    }
    setAuthDefaultView(view);
    setAuthOpen(true);
  };

  const handleServicesHover = (e) => {
    setServicesAnchor(e.currentTarget);
    setOpenServicesMenu(true);
  };

  const handleServicesClose = () => {
    setServicesAnchor(null);
    setOpenServicesMenu(false);
  };

  // ✅ Case Studies dropdown handlers
  const handleCaseStudiesHover = useCallback((e) => {
    setCaseStudiesAnchor(e.currentTarget);
    setOpenCaseStudiesMenu(true);
  }, []);

  const handleCaseStudiesClose = () => {
    setCaseStudiesAnchor(null);
    setOpenCaseStudiesMenu(false);
  };

  useEffect(() => {
    setIsAuthenticated(isLoggedIn());
    setAuthUser(getAuthUser());

    const onAuthChanged = (e) => {
      const authed = e?.detail?.isAuthenticated === true;
      setIsAuthenticated(authed);
      if (authed) {
        setAuthUser(e?.detail?.user || null);
        setAuthSuccessMessage("Successfully signed in.");
      } else {
        setAuthUser(null);
      }
    };

    const onOpenAuthModal = (e) => {
      if (isChatbotMaintenanceMode) {
        if (location.pathname !== MAINTENANCE_PAGE_PATH) {
          navigate(MAINTENANCE_PAGE_PATH);
        }
        return;
      }
      const view = e?.detail?.view || "signIn";
      setAuthDefaultView(view);
      setAuthOpen(true);
    };

    window.addEventListener("auth-changed", onAuthChanged);
    window.addEventListener("open-auth-modal", onOpenAuthModal);
    return () => {
      window.removeEventListener("auth-changed", onAuthChanged);
      window.removeEventListener("open-auth-modal", onOpenAuthModal);
    };
  }, [location.pathname, navigate]);

  const handleSignOut = () => {
    clearAuth();
    setIsAuthenticated(false);
    setAuthUser(null);
    handleAuthMenuClose();
    window.dispatchEvent(
      new CustomEvent("auth-changed", { detail: { isAuthenticated: false } })
    );
    navigate("/");
  };

  const routes = useMemo(
    () => [
      { label: "News & Events", link: "/news-events", activeTabIndex: 0 },
      {
        label: "Home",
        link: "/",
        activeTabIndex: 1,
        mouseover: handleHomeHover,
        hasDropdown: true,
        isDropdownOpen: openHomeMenu,
      },
      {
        label: "Services",
        link: "/services",
        activeTabIndex: 2,
        mouseover: handleServicesHover,
        hasDropdown: true,
        isDropdownOpen: openServicesMenu,
      },
      { label: "Careers", link: "/careers", activeTabIndex: 6 },
      { label: "Contact Us", link: "/contact", activeTabIndex: 5 },
      { label: "Get Estimate", link: "/get-estimate", activeTabIndex: 8 },
      { label: "Our Clients ", link: "/our-client", activeTabIndex: 9 },
      {
        label: "Case Studies ",
        link: "/case-studies",
        activeTabIndex: 10,
        mouseover: handleCaseStudiesHover,
        hasDropdown: true,
        isDropdownOpen: openCaseStudiesMenu,
      },
    ],
    [
      handleHomeHover,
      openHomeMenu,
      openServicesMenu,
      openCaseStudiesMenu,
      handleCaseStudiesHover,
    ]
  );

  // ✅ FIX: Normalize path correctly so "/" is never stripped to ""
  useEffect(() => {
    const path =
      location.pathname === "/" ? "/" : location.pathname.replace(/\/+$/, "");

    if (path === "/admin") {
      setValue(false);
      return;
    }

    if (path === "/ai-chatbot") {
      setValue(false);
      return;
    }

    const matchedRoute = routes.find((route) => route.link === path);
    if (matchedRoute) {
      setValue(matchedRoute.activeTabIndex);
      return;
    }

    if (path === "/about" || path === "/evolution" || path === "/partners") {
      setValue(1);
      return;
    }

    if (path === "/software" || path === "/websites") {
      setValue(2);
      return;
    }

    if (path === "/careers") {
      setValue(6);
      return;
    }

    // ✅ Case study detail pages (/case-studies/:id) keep the Case Studies tab active
    if (path.startsWith("/case-studies/")) {
      setValue(10);
      return;
    }

    // No match — clear the indicator
    setValue(false);
  }, [location.pathname, routes]);

  const handleChange = (e, newValue) => {
    setValue(newValue);
  };

  const goToAiChatbot = useCallback(() => {
    navigate({ pathname: "/ai-chatbot", search: "?launch=1" });
  }, [navigate]);

  // ----------------- Drawer (Mobile Menu) -----------------
  const DrawerMenu = (
    <>
      <SwipeableDrawer
        disableBackdropTransition={!iOS}
        disableDiscovery={iOS}
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        onOpen={() => setOpenDrawer(true)}
        anchor="right"
      >
        <ToolbarMargin />
        <List disablePadding>
          <ListItem
            button
            component={Link}
            to="/news-events"
            selected={location.pathname === "/news-events"}
            onClick={() => {
              setOpenDrawer(false);
              setValue(0);
            }}
            sx={{
              "&.Mui-selected .MuiListItemText-primary": { color: "#d32f2f" },
              "&:hover .MuiListItemText-primary": { color: "#d32f2f" },
            }}
          >
            <ListItemText
              primary="News & Events"
              primaryTypographyProps={{
                sx: {
                  color: "#d32f2f",
                  fontWeight: 800,
                  fontSize: "0.85rem",
                  animation: `${blink} 1s step-start infinite`,
                },
              }}
            />
          </ListItem>
          <ListItem
            button
            component={Link}
            to="/"
            selected={value === 1}
            onClick={() => {
              setOpenDrawer(false);
              setValue(1);
            }}
          >
            <ListItemText primary="Home" />
          </ListItem>
          <ListItem
            button
            component={Link}
            to="/about"
            onClick={() => setOpenDrawer(false)}
          >
            <ListItemText primary="About" />
          </ListItem>
          <ListItem
            button
            component={Link}
            to="/evolution"
            onClick={() => setOpenDrawer(false)}
          >
            <ListItemText primary="Evolution" />
          </ListItem>
          <ListItem
            button
            component={Link}
            to="/partners"
            onClick={() => setOpenDrawer(false)}
          >
            <ListItemText primary="Partners" />
          </ListItem>

          {/* Services with nested menu in mobile */}
          <ListItem button onClick={() => setServicesOpen(!servicesOpen)}>
            <ListItemText primary="Services" />
            <ExpandMoreIcon
              sx={{
                fontSize: "1rem",
                transition: "transform 0.2s ease",
                transform: servicesOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </ListItem>
          <Collapse in={servicesOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem
                button
                component={Link}
                to="/software"
                onClick={() => setOpenDrawer(false)}
                sx={{ pl: 4 }}
              >
                <ListItemText primary="Cyber Security" />
              </ListItem>
              <ListItem
                button
                component={Link}
                to="/websites"
                onClick={() => setOpenDrawer(false)}
                sx={{ pl: 4 }}
              >
                <ListItemText primary="Software Development" />
              </ListItem>
            </List>
          </Collapse>

          {routes
            .filter(
              (route) =>
                ![
                  "Home",
                  "News & Events",
                  "Services",
                  "Case Studies ",
                ].includes(route.label)
            )
            .map((route) => (
              <ListItem
                key={route.activeTabIndex}
                button
                component={Link}
                to={route.link}
                selected={value === route.activeTabIndex}
                onClick={() => {
                  setOpenDrawer(false);
                  setValue(route.activeTabIndex);
                }}
              >
                <ListItemText primary={route.label} />
              </ListItem>
            ))}

          {/* ✅ Case Studies with nested id-based menu in mobile */}
          <ListItem
            button
            onClick={() => setCaseStudiesDrawerOpen(!caseStudiesDrawerOpen)}
          >
            <ListItemText primary="Case Studies" />
            <ExpandMoreIcon
              sx={{
                fontSize: "1rem",
                transition: "transform 0.2s ease",
                transform: caseStudiesDrawerOpen
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
              }}
            />
          </ListItem>
          <Collapse in={caseStudiesDrawerOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {CASE_STUDIES.map((cs) => (
                <ListItem
                  key={cs.slug}
                  button
                  component={Link}
                  to={`/case-studies/${cs.slug}`}
                  selected={location.pathname === `/case-studies/${cs.slug}`}
                  onClick={() => {
                    setOpenDrawer(false);
                    setValue(10);
                  }}
                  sx={{ pl: 4 }}
                >
                  <ListItemText primary={cs.label} />
                </ListItem>
              ))}
            </List>
          </Collapse>

          <ListItem
            button
            component={Link}
            to="/get-estimate"
            selected={value === 8}
            onClick={() => {
              setOpenDrawer(false);
              setValue(8);
            }}
          >
            <ListItemText primary="Get Estimate" />
          </ListItem>

          <ListItem
            button
            component={Link}
            to="/ai-chatbot"
            selected={value === 7}
            onClick={() => {
              setOpenDrawer(false);
              setValue(false);
              goToAiChatbot();
            }}
          >
            <ListItemText primary="AI Chatbot" />
          </ListItem>
        </List>
      </SwipeableDrawer>
      <Box sx={{ ml: "auto", display: { xs: "block", md: "none" } }}>
        <IconButton
          onClick={() => setOpenDrawer(!openDrawer)}
          aria-label="menu"
        >
          <MenuIcon />
        </IconButton>
      </Box>
    </>
  );

  // ----------------- Tabs Menu (Desktop) -----------------
  const TabsMenu = (
    <Box sx={{ display: "flex", alignItems: "center", ml: "auto", gap: 2 }}>
      <Tabs
        value={typeof value === "number" && value !== false ? value : false}
        onChange={handleChange}
        textColor="inherit"
        indicatorColor="primary"
        // ✅ FIX: Always show the indicator — removed the value === 0 transparent hack
        sx={{
          "& .MuiTabs-indicator": {
            backgroundColor: "primary.main",
          },
        }}
      >
        {routes.map((route) => (
          <TabStyled
            onMouseOver={!isMobile ? route.mouseover : undefined}
            onMouseLeave={
              !isMobile
                ? ["Home", "Services", "Case Studies "].includes(route.label)
                  ? () => {}
                  : undefined
                : undefined
            }
            key={route.activeTabIndex}
            label={
              route.hasDropdown ? (
                <LabelWithArrow
                  label={route.label}
                  isOpen={route.isDropdownOpen}
                />
              ) : (
                route.label
              )
            }
            component={Link}
            to={route.link}
            value={route.activeTabIndex}
            sx={
              route.label === "News & Events"
                ? {
                    color: "#d32f2f",
                    "&.Mui-selected": { color: "#d32f2f" },
                    "&:hover": { color: "#d32f2f" },
                    animation: `${blink} 1s step-start infinite`,
                  }
                : undefined
            }
          />
        ))}
      </Tabs>
      <EstimateButton onClick={goToAiChatbot} variant="contained">
        AI Chatbot
      </EstimateButton>
      {/* Profile / Auth icon */}
      <IconButton aria-label="account" onClick={handleAuthIconClick}>
        <AccountCircleIcon color={isAuthenticated ? "primary" : undefined} />
      </IconButton>
      <Menu
        anchorEl={authMenuAnchor}
        open={Boolean(authMenuAnchor)}
        onClose={handleAuthMenuClose}
        sx={{ mt: 1.5 }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {!isAuthenticated && (
          <>
            <MenuItem
              onClick={() => openAuthWith("signIn")}
              sx={{ fontSize: "0.75rem" }}
            >
              Sign In
            </MenuItem>
            <MenuItem
              onClick={() => openAuthWith("signUp")}
              sx={{ fontSize: "0.75rem" }}
            >
              Sign Up
            </MenuItem>
          </>
        )}
        {isAuthenticated && (
          <>
            <MenuItem
              disableRipple
              sx={{
                fontSize: "0.75rem",
                color: "primary.main",
                fontWeight: 600,
                pointerEvents: "none",
              }}
            >
              {authUser?.email || "Signed In"}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setChangePwOpen(true);
                handleAuthMenuClose();
              }}
              sx={{ fontSize: "0.75rem" }}
            >
              Change Password
            </MenuItem>
            <MenuItem onClick={handleSignOut} sx={{ fontSize: "0.75rem" }}>
              Sign Out
            </MenuItem>
          </>
        )}
      </Menu>
      <Popper
        open={openHomeMenu}
        anchorEl={homeAnchor}
        transition
        placement="bottom-start"
        modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
        style={{ zIndex: 1400 }} // ← above AppBar's z-index of 1301
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: "top left" }}>
            <Paper sx={{ minWidth: 180 }}>
              <ClickAwayListener onClickAway={handleMenuClose}>
                <MenuList
                  onMouseLeave={handleMenuClose} // ← close when leaving the list
                  sx={{ p: 0 }}
                >
                  <MenuItem
                    component={Link}
                    to="/about"
                    onClick={handleMenuClose}
                    selected={location.pathname === "/about"}
                    sx={{
                      fontSize: "0.75rem",
                      "&:hover": { bgcolor: "primary.main", color: "#fff" },
                      "&.Mui-selected": {
                        bgcolor: "primary.main",
                        color: "#fff",
                      },
                      "&.Mui-selected:hover": {
                        bgcolor: "primary.main",
                        color: "#fff",
                      },
                    }}
                  >
                    About
                  </MenuItem>
                  <MenuItem
                    component={Link}
                    to="/evolution"
                    onClick={handleMenuClose}
                    selected={location.pathname === "/evolution"}
                    sx={{
                      fontSize: "0.75rem",
                      "&:hover": { bgcolor: "primary.main", color: "#fff" },
                      "&.Mui-selected": {
                        bgcolor: "primary.main",
                        color: "#fff",
                      },
                      "&.Mui-selected:hover": {
                        bgcolor: "primary.main",
                        color: "#fff",
                      },
                    }}
                  >
                    Evolution
                  </MenuItem>
                  <MenuItem
                    component={Link}
                    to="/partners"
                    onClick={handleMenuClose}
                    selected={location.pathname === "/partners"}
                    sx={{
                      fontSize: "0.75rem",
                      "&:hover": { bgcolor: "primary.main", color: "#fff" },
                      "&.Mui-selected": {
                        bgcolor: "primary.main",
                        color: "#fff",
                      },
                      "&.Mui-selected:hover": {
                        bgcolor: "primary.main",
                        color: "#fff",
                      },
                    }}
                  >
                    Partners
                  </MenuItem>{" "}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      {/* Services Submenu */}
      {!isMobile && (
        <Popper
          open={openServicesMenu}
          anchorEl={servicesAnchor}
          transition
          placement="bottom-start"
          modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
          style={{ zIndex: 1400 }}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} style={{ transformOrigin: "top left" }}>
              <Paper sx={{ minWidth: 200 }}>
                <ClickAwayListener onClickAway={handleServicesClose}>
                  <MenuList
                    onMouseLeave={handleServicesClose} // ← close when leaving the list
                    sx={{ p: 0 }}
                  >
                    <MenuItem
                      component={Link}
                      to="/software"
                      onClick={handleServicesClose}
                      selected={location.pathname === "/software"}
                      sx={{
                        fontSize: "0.75rem",
                        "&:hover": { bgcolor: "primary.main", color: "#fff" },
                        "&.Mui-selected": {
                          bgcolor: "primary.main",
                          color: "#fff",
                        },
                        "&.Mui-selected:hover": {
                          bgcolor: "primary.main",
                          color: "#fff",
                        },
                      }}
                    >
                      Cyber Security
                    </MenuItem>
                    <MenuItem
                      component={Link}
                      to="/websites"
                      onClick={handleServicesClose}
                      selected={location.pathname === "/websites"}
                      sx={{
                        fontSize: "0.75rem",
                        "&:hover": { bgcolor: "primary.main", color: "#fff" },
                        "&.Mui-selected": {
                          bgcolor: "primary.main",
                          color: "#fff",
                        },
                        "&.Mui-selected:hover": {
                          bgcolor: "primary.main",
                          color: "#fff",
                        },
                      }}
                    >
                      Software Development
                    </MenuItem>{" "}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
      {/* ✅ Case Studies Submenu (id-based) */}
      {!isMobile && (
        <Popper
          open={openCaseStudiesMenu}
          anchorEl={caseStudiesAnchor}
          transition
          placement="bottom-start"
          modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
          style={{ zIndex: 1400 }}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} style={{ transformOrigin: "top left" }}>
              <Paper sx={{ minWidth: 220 }}>
                <ClickAwayListener onClickAway={handleCaseStudiesClose}>
                  <MenuList onMouseLeave={handleCaseStudiesClose} sx={{ p: 0 }}>
                    {CASE_STUDIES.map((cs) => (
                      <MenuItem
                        key={cs.slug}
                        component={Link}
                        to={`/case-studies/${cs.slug}`}
                        onClick={() => {
                          handleCaseStudiesClose();
                          setValue(10);
                        }}
                        selected={
                          location.pathname === `/case-studies/${cs.slug}`
                        }
                        sx={{
                          fontSize: "0.75rem",
                          "&:hover": {
                            bgcolor: "primary.main",
                            color: "#fff",
                          },
                          "&.Mui-selected": {
                            bgcolor: "primary.main",
                            color: "#fff",
                          },
                          "&.Mui-selected:hover": {
                            bgcolor: "primary.main",
                            color: "#fff",
                          },
                        }}
                      >
                        {cs.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </Box>
  );

  return (
    <>
      <ElevationScroll>
        <AppBarStyled position="fixed" elevation={0}>
          <Toolbar disableGutters sx={{ px: 2 }}>
            <Button onClick={() => setValue(1)} component={Link} to="/">
              <Box
                sx={{
                  p: 0.5,
                  borderRadius: 2,
                  boxShadow: "none",
                  transition: "box-shadow 0.3s ease",
                }}
              >
                <Logo src={logo} alt="logo" />
              </Box>
            </Button>
            {isMobile ? (
              <>
                <Box
                  sx={{
                    ml: "auto",
                    display: { xs: "flex", md: "none" },
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <IconButton
                    aria-label="account"
                    onClick={handleAuthIconClick}
                  >
                    <AccountCircleIcon />
                  </IconButton>
                  {DrawerMenu}
                </Box>
                <Menu
                  anchorEl={authMenuAnchor}
                  open={Boolean(authMenuAnchor)}
                  onClose={handleAuthMenuClose}
                  sx={{ mt: 1.5 }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  {!isAuthenticated && (
                    <>
                      <MenuItem
                        onClick={() => openAuthWith("signIn")}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Sign In
                      </MenuItem>
                      <MenuItem
                        onClick={() => openAuthWith("signUp")}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Sign Up
                      </MenuItem>
                    </>
                  )}
                  {isAuthenticated && (
                    <>
                      <MenuItem
                        disableRipple
                        sx={{
                          fontSize: "0.75rem",
                          color: "primary.main",
                          fontWeight: 600,
                          pointerEvents: "none",
                        }}
                      >
                        {authUser?.email || "Signed In"}
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setChangePwOpen(true);
                          handleAuthMenuClose();
                        }}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Change Password
                      </MenuItem>
                      <MenuItem
                        onClick={handleSignOut}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Sign Out
                      </MenuItem>
                    </>
                  )}
                </Menu>
              </>
            ) : (
              TabsMenu
            )}
          </Toolbar>
        </AppBarStyled>
      </ElevationScroll>
      <ToolbarMargin />

      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultView={authDefaultView}
      />
      <ChangePasswordDialog
        open={changePwOpen}
        onClose={() => setChangePwOpen(false)}
      />
      <Snackbar
        open={Boolean(authSuccessMessage)}
        autoHideDuration={3000}
        onClose={() => setAuthSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setAuthSuccessMessage("")}
          sx={{ borderRadius: "12px", fontWeight: 500 }}
        >
          {authSuccessMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Header;
